import {Injectable} from '@angular/core';
import {buildPath} from '@utils/util-builder';
import { HttpClient, HttpParams } from '@angular/common/http';
import {Observable, map} from 'rxjs';
import {RPStatus} from '@enums/RPStatus';
import {ContractResponse} from '@models/contract.model';
import {ApiResponse} from '@models/api.response';
import {Page, paginatorToHttpParams} from '@utils/paginator-util';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {ContractStatus} from '@models/contract-status.interface';
import {RangeDates} from '@models/range-dates.interface';

@Injectable({
    providedIn: 'root'
})
export class ContractService {
    private readonly _basePath: string = 'data-core/contracts';

    constructor(private readonly _http: HttpClient) {
    }

    getContracts(contracteeId: number, contractorId: number, paginator: MatPaginator, sortState: MatSort): Observable<Page<ContractResponse>> {
        let params: HttpParams = paginatorToHttpParams(paginator, undefined, sortState);
        params = params.set('contracteeId', contracteeId?.toString())
        params = params.set('contractorId', contractorId.toString());

        return this._http.get<ApiResponse<Page<ContractResponse>>>(buildPath([this._basePath]), {params})
            .pipe(map((response: ApiResponse<Page<ContractResponse>>): Page<ContractResponse> => response.data));
    }

    getContractByContractImssRegistryId(imssEmployerRegistryId: number): Observable<ContractResponse> {
        const url: string = buildPath([this._basePath, 'by-imss-employer-registry', imssEmployerRegistryId.toString()]);
        return this._http.get<ApiResponse<ContractResponse>>(url)
            .pipe(map((response: ApiResponse<ContractResponse>): ContractResponse => response.data));
    }

    downloadContract(fileName: string): Observable<Blob> {
        return this._http.get(buildPath(['data-core/Company/getFileByName/' + fileName]), {responseType: 'blob'});
    }

    createContract(contract: FormData): Observable<ContractResponse> {
        return this._http.post<ApiResponse<ContractResponse>>(buildPath([this._basePath]), contract)
            .pipe(map((response: ApiResponse<ContractResponse>): ContractResponse => response.data));
    }

    updateContract(contract: FormData, contractId: number): Observable<ContractResponse> {
        return this._http.put<ApiResponse<ContractResponse>>(buildPath([this._basePath, contractId.toString()]), contract)
            .pipe(map((response: ApiResponse<ContractResponse>): ContractResponse => response.data));
    }

    deleteContract(id: number): Observable<void> {
        return this._http.delete<void>(buildPath([this._basePath, id.toString()]));
    }

    setRpStatus(id: number, rpStatusToSet: RPStatus): Observable<RPStatus> {
        const url: string = buildPath([this._basePath, id.toString(), 'set-rp-status']);
        return this._http.patch<ApiResponse<RPStatus>>(url, {rpStatus: rpStatusToSet})
            .pipe(map((response: ApiResponse<RPStatus>): RPStatus => response.data));
    }

    addRpToContract(contractId: number, imssEmployerRegistryId: number): Observable<ContractResponse> {
        const url: string = buildPath([this._basePath, contractId.toString(), 'imss-employer-registry']);
        return this._http.patch<ApiResponse<ContractResponse>>(url, {imssEmployerRegistryId})
            .pipe(map((response: ApiResponse<ContractResponse>): ContractResponse => response.data));
    }

    getStatusList(): Observable<ContractStatus[]> {
        return this._http.get<ApiResponse<ContractStatus[]>>(buildPath([this._basePath, 'status-list']))
            .pipe(map((response: ApiResponse<ContractStatus[]>): ContractStatus[] => response.data));
    }

    getContractsDateRangeByContractor(contractorId: string): Observable<RangeDates> {
        let params: HttpParams = new HttpParams();
        if (contractorId) {
            params = params.set('contractorId', contractorId);
        }
        const url: string = buildPath([this._basePath, 'date-range/contractor']);
        return this._http.get<ApiResponse<RangeDates>>(url, {params})
            .pipe(map((response: ApiResponse<RangeDates>): RangeDates => response.data));
    }
}
